import React, { useEffect } from 'react';
import {
	useProjectInvitations,
	ProjectInvitationStatus,
	ProjectInvitation
} from './useProjectInvitations';
import { useLanguage } from 'features/hooks/useLanguage';
import { LanguageType } from 'types/index';
import { formatDate } from 'utils/date';
import { useUsersByEmails } from 'hooks/store';
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow
} from 'features/components/Table';
import { Button } from 'features/components/Button';
import { UserDetails } from 'api/data/accounts';

interface ProjectInvitationsTableProps {
	projectId: string;
}

const DICTIONARY = {
	[LanguageType.English]: {
		loading: 'Loading invitations...',
		failedToLoad: 'Failed to load project invitations',
		projectInvitations: 'Project Invitations',
		manageInvitations: 'Manage all pending and historical project invitations',
		invitedEmail: 'Invited Email',
		invitedBy: 'Invited By',
		status: 'Status',
		revokedBy: 'Revoked By',
		expirationDate: 'Expiration Date',
		actions: 'Actions',
		revoke: 'Revoke',
		showing: 'Showing',
		invitation: 'invitation',
		invitations: 'invitations'
	},
	[LanguageType.Norwegian]: {
		loading: 'Laster invitasjoner...',
		failedToLoad: 'Kunne ikke laste prosjektinvitasjoner',
		projectInvitations: 'Prosjektinvitasjoner',
		manageInvitations: 'Administrer alle ventende og historiske prosjektinvitasjoner',
		invitedEmail: 'Invitert e-post',
		invitedBy: 'Invitert av',
		status: 'Status',
		revokedBy: 'Tilbakekalt av',
		expirationDate: 'Utløpsdato',
		actions: 'Handlinger',
		revoke: 'Tilbakekall',
		showing: 'Viser',
		invitation: 'invitasjon',
		invitations: 'invitasjoner'
	}
};

export const ProjectInvitationsTable: React.FC<ProjectInvitationsTableProps> = ({ projectId }) => {
	const { invitations, isLoading, isQueryError, revokeInvitation, revokeInvitationMutation } =
		useProjectInvitations(Number(projectId));

	const [
		{
			data: { newUsersData },
			loading: gettingUsersByEmails
		},
		getUsersByEmails
	] = useUsersByEmails(invitations?.map(inv => inv.invitedUserEmail) ?? []);

	useEffect(() => {
		if (invitations && invitations.length > 0) {
			getUsersByEmails(invitations?.map(inv => inv.invitedUserEmail) ?? []);
		}
	}, [invitations]);

	const language = useLanguage();
	const dictionary = DICTIONARY[language];

	const handleRevokeInvitation = async (invitationId: number) => {
		try {
			await revokeInvitation({ invitationId });
		} catch (error) {
			console.error('Failed to revoke invitation:', error);
		}
	};

	const getStatusColor = (status: string) => {
		switch (status) {
			case ProjectInvitationStatus.PENDING:
				return 'text-yellow-600 bg-yellow-100 border border-yellow-200';
			case ProjectInvitationStatus.ACCEPTED:
				return 'text-green-600 bg-green-100 border border-green-200';
			case ProjectInvitationStatus.EXPIRED:
				return 'text-gray-600 bg-gray-100 border border-gray-200';
			case ProjectInvitationStatus.REVOKED:
				return 'text-red-600 bg-red-100 border border-red-200';
			case ProjectInvitationStatus.REJECTED:
				return 'text-red-600 bg-red-100 border border-red-200';
			default:
				return 'text-gray-600';
		}
	};

	const renderUserNameCell = (
		invitation: ProjectInvitation,
		userData: UserDetails | undefined
	) => {
		if (gettingUsersByEmails) {
			return (
				<div className="flex flex-col animate-pulse">
					<div className="h-5 w-32 bg-gray-200 rounded"></div>
					<span className="text-sm text-gray-500 mt-1">
						{invitation.invitedUserEmail}
					</span>
				</div>
			);
		}

		if (userData) {
			return (
				<div className="flex flex-col">
					<span>
						{userData.userFirstName} {userData.userSirName}
					</span>
					<span className="text-sm text-gray-500">{invitation.invitedUserEmail}</span>
				</div>
			);
		}

		return invitation.invitedUserEmail;
	};

	if (isLoading) {
		return (
			<div className="w-full flex items-center justify-center p-10">
				<div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-500"></div>
				<p className="mt-4 text-gray-600 font-medium">{dictionary.loading}</p>
			</div>
		);
	}

	if (isQueryError) {
		return (
			<div className="w-full rounded-xl shadow-md p-5 bg-red-50 border border-red-200">
				<p className="text-red-600 font-medium text-center">{dictionary.failedToLoad}</p>
			</div>
		);
	}

	return (
		<div className="flex flex-col gap-4">
			<div>
				<span className="table-info">
					{dictionary.showing} {invitations?.length || 0}{' '}
					{invitations?.length !== 1 ? dictionary.invitations : dictionary.invitation}
				</span>
			</div>
			<Table>
				<TableHeader>
					<TableRow>
						<TableHead>
							<div className="table-header-label">{dictionary.invitedEmail}</div>
						</TableHead>
						<TableHead>
							<div className="table-header-label">{dictionary.invitedBy}</div>
						</TableHead>
						<TableHead>
							<div className="table-header-label">{dictionary.status}</div>
						</TableHead>
						<TableHead>
							<div className="table-header-label">{dictionary.revokedBy}</div>
						</TableHead>
						<TableHead>
							<div className="table-header-label">{dictionary.expirationDate}</div>
						</TableHead>
						<TableHead>
							<div className="table-header-label">{dictionary.actions}</div>
						</TableHead>
					</TableRow>
				</TableHeader>
				<TableBody>
					{invitations &&
						invitations.map(invitation => {
							const userData = newUsersData?.find(
								user => user.emailAddress === invitation.invitedUserEmail
							);
							return (
								<TableRow key={invitation.invitationId}>
									<TableCell>
										{renderUserNameCell(invitation, userData)}
									</TableCell>
									<TableCell>
										{invitation.inviterNameAtTheTimeOfTheInvitation}
									</TableCell>
									<TableCell>
										<span
											className={`px-3 py-1.5 text-xs rounded-full ${getStatusColor(
												invitation.status
											)} shadow-sm`}
										>
											{invitation.status}
										</span>
									</TableCell>
									<TableCell>{invitation.revokedByNameAtTime}</TableCell>
									<TableCell>{formatDate(invitation.expiresAt)}</TableCell>
									<TableCell>
										{invitation.status === ProjectInvitationStatus.PENDING && (
											<Button
												variant="destructive"
												text={dictionary.revoke}
												onClick={() =>
													handleRevokeInvitation(invitation.invitationId)
												}
												loading={
													revokeInvitationMutation.isLoading &&
													revokeInvitationMutation.variables
														?.invitationId === invitation.invitationId
												}
											/>
										)}
									</TableCell>
								</TableRow>
							);
						})}
				</TableBody>
			</Table>
		</div>
	);
};
